// The canonical organization compliance profile.
//
// THE ONE THING THIS SCREEN MUST GET RIGHT is the distinction between what is
// true NOW and what somebody SIGNED. Editing an address here does not alter a
// statement issued last March, and an operator who believes otherwise will
// eventually "fix" a record to correct a document and achieve nothing. The
// header says so in words, and submitted statements are shown as a separate,
// visibly read-only list rather than mixed in with the editable fields.
//
// Sensitive values are masked. A tax identifier shows as •••• plus its last
// four; revealing it is a deliberate, owner-only, audited action and is not
// offered here.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertCircle, Building2, FileCheck, Lock, MapPin, ShieldCheck, Users,
} from 'lucide-react';
import {
  getPartnerComplianceProfile,
  type PartnerComplianceOwner,
  type PartnerComplianceRegistration,
} from '@/lib/api';

const ROLE_LABEL: Record<string, string> = {
  compliance: 'Compliance',
  purchasing: 'Purchasing',
  accounting: 'Accounting',
  shipping: 'Shipping',
  signer: 'Signer',
  executive: 'Executive',
  general: 'General',
};

const RELATIONSHIP_LABEL: Record<string, string> = {
  beneficial_owner: 'Beneficial owner',
  shareholder: 'Shareholder',
  officer: 'Officer',
  director: 'Director',
  signatory: 'Signatory',
};

const ADDRESS_KIND_LABEL: Record<string, string> = {
  registered: 'Registered',
  headquarters: 'Headquarters',
  billing: 'Billing',
  shipping: 'Shipping',
  remit: 'Remit-to',
};

function Section({
  icon, title, count, children,
}: {
  icon: React.ReactNode; title: string; count?: number; children: React.ReactNode;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-foreground-muted">{icon}</span>
        <h3 className="text-sm font-semibold">{title}</h3>
        {count !== undefined && (
          <span className="text-[11px] text-foreground-muted tabular-nums">({count})</span>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-foreground-muted">{children}</p>;
}

/** "•••• 6789" — enough to reconcile against a document, useless on its own. */
function MaskedTaxId({ reg }: { reg: PartnerComplianceRegistration }) {
  if (!reg.taxIdLast4) return <span className="text-foreground-muted">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs">
      <Lock className="w-3 h-3 text-foreground-muted" aria-hidden />
      ••••&nbsp;{reg.taxIdLast4}
      {reg.taxIdKind && (
        <span className="text-foreground-muted uppercase">{reg.taxIdKind}</span>
      )}
    </span>
  );
}

function ownerName(o: PartnerComplianceOwner): string {
  if (o.holderKind === 'organization') return o.organizationName ?? '(unnamed company)';
  return [o.firstName, o.surname].filter(Boolean).join(' ') || '(unnamed person)';
}

export function ComplianceProfilePanel({ partnerPublicId }: { partnerPublicId: string }) {
  const { getToken } = useAuth();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['partner-compliance', partnerPublicId],
    queryFn: () => getPartnerComplianceProfile(partnerPublicId, () => getToken()),
  });

  if (isLoading) {
    return <div className="card p-4 text-xs text-foreground-muted">Loading compliance profile…</div>;
  }

  if (isError) {
    return (
      <div className="card p-4 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" aria-hidden />
        <div className="text-xs">
          <div className="font-medium">Could not load the compliance profile</div>
          <div className="text-foreground-muted">
            {(error as { message?: string })?.message ?? 'Unknown error'}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { contacts, registrations, owners, addresses, completeness } = data;
  const activeOwners = owners.filter((o) => o.effectiveTo === null);

  return (
    <div className="space-y-3">
      {/* The distinction the whole feature depends on. */}
      <div className="card p-3 bg-info-muted">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-primary-strong mt-0.5 flex-shrink-0" aria-hidden />
          <p className="text-[11px] leading-relaxed">
            <span className="font-medium">This is the company record as it stands today.</span>{' '}
            Editing anything here does not change a compliance statement that has already been
            submitted — those are snapshots of what was declared at the time, and they are
            listed separately.
          </p>
        </div>
      </div>

      {/* Completeness, so an operator can see what is missing before a supplier asks. */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold mb-3">Profile completeness</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <Check ok={completeness.hasRegisteredAddress} label="Registered address on file" />
          <Check ok={completeness.hasComplianceContact} label="Compliance contact named" />
          <Check ok={completeness.hasPrimaryRegistration} label="Primary registration recorded" />
          <Check
            ok={completeness.ownerCount > 0}
            label={`Owners / officers recorded (${completeness.ownerCount})`}
          />
        </div>
        {completeness.ownerCount > 0 && (
          <p className="text-[11px] text-foreground-muted mt-2">
            Equity recorded: {completeness.equityRecordedPct}%
            {completeness.equityRecordedPct !== 100 && (
              <span> — a KYC form that asks for 100% will need the remainder.</span>
            )}
          </p>
        )}
      </div>

      <Section icon={<MapPin className="w-4 h-4" />} title="Addresses" count={addresses.length}>
        {addresses.length === 0 ? (
          <Empty>No addresses recorded. A KYC form asks for the registered address.</Empty>
        ) : (
          <ul className="space-y-2">
            {addresses.map((a) => (
              <li key={a.publicId} className="text-xs">
                <span className="pill pill-default mr-2">{ADDRESS_KIND_LABEL[a.kind] ?? a.kind}</span>
                <span>{a.line1}</span>
                {a.city && <span className="text-foreground-muted">, {a.city}</span>}
                {a.countryCode && <span className="text-foreground-muted"> {a.countryCode}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={<Building2 className="w-4 h-4" />} title="Registrations" count={registrations.length}>
        {registrations.length === 0 ? (
          <Empty>No registrations recorded.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[520px] w-full text-xs">
              <thead>
                <tr className="text-foreground-muted text-left">
                  <th className="py-1 pr-3 font-medium">Jurisdiction</th>
                  <th className="py-1 pr-3 font-medium">Type</th>
                  <th className="py-1 pr-3 font-medium">Number</th>
                  <th className="py-1 pr-3 font-medium">Tax ID</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.publicId} className="border-t border-border">
                    <td className="py-1.5 pr-3">
                      {r.jurisdictionCountry}
                      {r.jurisdictionSubdivision && (
                        <span className="text-foreground-muted"> / {r.jurisdictionSubdivision}</span>
                      )}
                      {r.isPrimary && <span className="pill pill-primary ml-2">Primary</span>}
                    </td>
                    <td className="py-1.5 pr-3">{r.legalEntityType ?? r.kind}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.registrationNumber ?? '—'}</td>
                    <td className="py-1.5 pr-3"><MaskedTaxId reg={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section icon={<Users className="w-4 h-4" />} title="Owners, officers and directors" count={activeOwners.length}>
        {activeOwners.length === 0 ? (
          <Empty>
            None recorded. A KYC form typically requires every individual or entity holding an
            ownership interest.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {activeOwners.map((o) => (
              <li key={o.publicId} className="text-xs flex items-start gap-2">
                <span className="pill pill-default flex-shrink-0">
                  {RELATIONSHIP_LABEL[o.relationship] ?? o.relationship}
                </span>
                <span className="min-w-0">
                  <span className="font-medium">{ownerName(o)}</span>
                  {o.title && <span className="text-foreground-muted"> · {o.title}</span>}
                  {o.ownershipPct && (
                    <span className="text-foreground-muted tabular-nums"> · {o.ownershipPct}%</span>
                  )}
                  {o.citizenshipCountry && (
                    <span className="text-foreground-muted"> · {o.citizenshipCountry}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-foreground-muted mt-3 flex items-center gap-1">
          <Lock className="w-3 h-3" aria-hidden />
          Dates of birth are stored encrypted and are not shown here.
        </p>
      </Section>

      <Section icon={<FileCheck className="w-4 h-4" />} title="Compliance contacts" count={contacts.length}>
        {contacts.length === 0 ? (
          <Empty>
            No compliance contacts. These are separate from RFQ contacts — the person who signs a
            supplier form is often not the person who sends the enquiry.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {contacts.map((c) => (
              <li key={c.publicId} className="text-xs">
                <span className={c.isActive ? 'font-medium' : 'font-medium text-foreground-muted line-through'}>
                  {c.fullName}
                </span>
                {c.title && <span className="text-foreground-muted"> · {c.title}</span>}
                <span className="ml-2">
                  {c.roles.map((r) => (
                    <span key={r} className="pill pill-default mr-1">{ROLE_LABEL[r] ?? r}</span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ok ? 'bg-success' : 'bg-warning'}`}
        aria-hidden
      />
      <span className={ok ? '' : 'text-foreground-muted'}>{label}</span>
      <span className="sr-only">{ok ? 'present' : 'missing'}</span>
    </div>
  );
}
